<template>
  <div class="inspector-page">
    <header class="inspector-header">
      <Button
        :to="{
          name: 'entity-documents',
          params: { entityExternalId: entityId },
        }"
        icon-before="fas fa-chevron-left"
        size="3"
      >
        Back to Documents
      </Button>

      <Title
        size="4"
        class="inspector-title"
      >
        Document Extraction
      </Title>

      <Buttons size="3">
        <o-tooltip
          variant="primary"
          position="left"
          :label="validationTooltip"
          multiline
        >
          <Button
            size="3"
            variant="primary"
            :loader="isLoading"
            @click="reRunValidation"
          >
            Re-run Doc CheckUp
          </Button>
        </o-tooltip>

        <Button
          size="3"
          :variant="failedValidations.length > 0 ? 'danger' : 'success'"
          :icon-before="failedValidations.length > 0 ? 'fas fa-exclamation-circle' : 'fas fa-check-circle'"
          :disabled="isLoading"
          @click="showValidationDrawer = true"
        >
          <span>{{ showValidationDrawer ? 'Hide' : 'Show' }} DocCheckup</span>
        </Button>
      </Buttons>
    </header>

    <div class="inspector-main">
      <div class="inspector-pdf">
        <InspectorDocument
          v-if="currentDoc"
          :document-url="currentDoc.download_url"
          :preview-url="currentDoc.preview_url"
          :file-type="currentDoc.file_type"
        />
      </div>

      <div class="inspector-lead">
        <div class="inspector-controls">
          <div class="controls-header">
            <div class="inspector-details">
              <strong v-if="currentDoc">{{ currentDoc.filename }}</strong>—
              <TextLink
                @click="showInfo = !showInfo"
              >
                <span v-if="showInfo">Hide details</span>
                <span v-else>Show details</span>
              </TextLink>
            </div>

            <div class="pages">
              <div v-if="inspectorStore.currentDocumentType">
                Type:<strong>{{ inspectorStore.currentDocumentType }}</strong>
              </div>
              <div v-else>
                Other fields
              </div>

              <div class="current-page">
                <span>Page</span>
                <strong>{{ inspectorStore.currentPage }}</strong>
                <span>of</span>
                <strong>{{ inspectorStore.totalPages }}</strong>
              </div>
              <IconButton
                class="page-prev"
                icon="fas fa-arrow-left"
                :disabled="inspectorStore.currentPage === 1"
                @click="inspectorStore.prevPage()"
              />
              <IconButton
                class="page-next"
                icon="fas fa-arrow-right"
                :disabled="inspectorStore.currentPage === inspectorStore.totalPages"
                @click="inspectorStore.nextPage()"
              />
            </div>
          </div>

          <p
            v-if="currentDoc && showInfo"
            class="inspector-info"
          >
            {{ currentDoc.info }}
          </p>
        </div>

        <div class="inspector-fields">
          <InspectorFields />
        </div>

        <footer class="inspector-pagination">
          <Buttons>
            <Button
              size="3_5"
              icon-before="fas fa-arrow-left"
              outlined
              :disabled="inspectorStore.currentPage === 1"
              @click="inspectorStore.prevPage()"
            >
              Previous
            </Button>
            <Button
              size="3_5"
              icon-after="fas fa-arrow-right"
              outlined
              :disabled="inspectorStore.currentPage === inspectorStore.totalPages"
              @click="inspectorStore.nextPage()"
            >
              Next Page
            </Button>
          </Buttons>

          <Button
            size="3_5"
            icon-before="fas fa-save"
            variant="primary"
            @click="onSaveChanges"
          >
            Save Changes
          </Button>
        </footer>
      </div>
    </div>

    <Drawer v-model="showValidationDrawer">
      <div class="inspector-validation">
        <ValidationDetails
          v-if="currentDoc"
          :validation-rules="validationRules"
          :document-type="currentDoc.classification"
          :full-classification="currentDoc.full_classification"
          :document-id="currentDoc.external_id"
          @validation-deleted="refreshValidationData"
        />
      </div>
    </Drawer>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useAdvisorStore } from '@stores/advisorStore.js';
import { useInspectorStore } from '@stores/documentInspectorStore';
import { Button, Buttons, Drawer, IconButton, TextLink, Title } from '@stakeui';

import ValidationDetails from '@frontend/ui/features/example/components/ValidationDetails.vue';
import InspectorDocument from './InspectorDocument.vue';
import InspectorFields from './InspectorFields.vue';

const advisorStore = useAdvisorStore();
const inspectorStore = useInspectorStore();

const currentDoc = computed(() => inspectorStore.getDocument(props.id));
const showInfo = ref(false);
const validationRules = ref([]);
const showValidationDrawer = ref(false);
const isLoading = ref(false);
const hasEdits = ref(false);
const validationPassed = ref(false);

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  entityId: {
    type: String,
    required: true,
  },
});

const formatRuleName = name => {
  if (!name) return '';
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const loadValidationStatus = async () => {
  const doc = currentDoc.value;
  const externalId = doc?.external_id;
  try {
    // Fetch validation results using the document's external ID
    const response = await fetch(`/api/v1/documents/${externalId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to load validation results');
    }

    const result = await response.json();

    // Check if validation exists and is an array
    // TODO: Uncomment below when we want to show validation status for all documents again
    if (Array.isArray(result.document[0].validation)) {
      validationRules.value = result.document[0].validation.map((rule, index) => ({
        id: `${rule.name}-${rule.start_page}-${rule.end_page}-${index}`, // Include page info for uniqueness
        name: formatRuleName(rule.name) || 'Unnamed Validation',
        description: rule.description || formatRuleName(rule.name) || 'Unnamed Validation',
        document_type: rule.document_type || result.document[0].classification?.document_type || result.document[0].classification?.classification || '',
        passed: rule.passed || false,
        details: rule.details || null,
        start_page: rule.start_page || null,
        end_page: rule.end_page || null,
      }));
    } else {
      // If no validation exists, initialize as an empty array
      validationRules.value = [];
    }
  } catch (error) {
    console.error('Error loading validation results:', error.message);
    alert('Failed to load validation results. Please try again.');
    validationRules.value = []; // Reset validation rules on error
  }

  // After loading validation rules, ensure drawer is open if there are rules
  if (validationRules.value.length > 0) {
    showValidationDrawer.value = true;
  }
};

const refreshValidationData = async () => {
  console.log('Refreshing validation data...');
  isLoading.value = true;

  try {
    // Refresh validation results
    await loadValidationStatus();
    console.log('Validation data refreshed, rules count:', validationRules.value.length);
    console.log('Updated validation rules:', validationRules.value);
  } finally {
    isLoading.value = false;
  }
};

const reRunValidation = async () => {
  isLoading.value = true;

  try {
    const externalId = currentDoc.value?.external_id
    // Make the POST request to the backend
    const response = await fetch(`/api/v1/documents/${externalId}/revalidate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...advisorStore.headers,
      },
    });

    // Check if the response is OK
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to re-run validation checks');
    }

    // Parse and process the response
    const result = await response.json();
    console.log('Validation Result:', result);

    // Update the validation status
    if (Array.isArray(result.validation)) {
      const allChecksPassed = result.validation.every(v => v.passed);
      validationPassed.value = allChecksPassed; // Update UI status
    } else {
      validationPassed.value = false; // Handle unexpected validation response
    }
  } catch (error) {
    console.error('Error re-running validation:', error.message);
    alert('Failed to re-run validation checks.');
  } finally {
    isLoading.value = false;
  }
};

const onSaveChanges = async () => {
  const payload = inspectorStore.submitForm();
  if (!payload) return;
  try {
    isLoading.value = true;
    const res = await fetch(
      `/api/v1/documents/${encodeURIComponent(payload.docupload_id)}/batch-edit-docupload`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...advisorStore.headers, // includes CSRF, auth, etc.
        },
        body: JSON.stringify(payload),
      },
    );

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.error || `Failed to save changes`);
    }

    const data = await res.json();
    console.log('[InspectorMain] Save successful', data);

    // TODO: can refresh docupload or validations here if you want
    // await inspectorStore.fetchDocupload(props.id);
    // await loadValidationStatus();

    hasEdits.value = false; // if you decide to track edits
    alert('Changes saved successfully.');
  } catch (e) {
    console.error('[InspectorMain] Error saving changes:', e);
    alert('Failed to save changes. Please try again.');
  } finally {
    isLoading.value = false;
  }
};


const failedValidations = computed(() => validationRules.value.filter(rule => !rule.passed));
const validationTooltip = computed(() => {
  if (!hasEdits.value) {
    return 'You can re-run Doc CheckUp after making edits';
  }

  return 'Click to re-run Doc CheckUp after making edits';
});


onMounted(async () => {
  await inspectorStore.fetchDocupload(props.id, { headers: { 'X-CSRFToken': advisorStore.csrfToken } });
  await nextTick();
  // Log what the component can see
  console.log('[InspectorMain] props.id =', props.id);
  console.log('[InspectorMain] currentDoc after fetch =', currentDoc.value);
  console.log('[InspectorMain] has keys:', Object.keys(currentDoc.value || {}));
  const externalId = currentDoc.value?.external_id
  if (externalId) await loadValidationStatus();
});
</script>



<style scoped>
.inspector-page {
  background-color: #F3F4F6;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

.inspector-header {
  background-color: #fff;
  box-shadow: var(--stk-control-shadow);
  flex-shrink: 0;
  flex-grow: 0;
  padding: 1em;
  position: relative;
  gap: 1em;
  display: flex;
  align-items: center;
  justify-content: start;
}

.inspector-title {
  flex-grow: 1;
}

.inspector-main {
  display: flex;
  overflow: hidden;
}

.inspector-pdf {
  overflow-x: hidden;
  overflow-y: auto;
  flex-shrink: 0;
  max-height: 100%;
  max-width: 50%;
  width: 100%;
  order: 2;
}

.inspector-lead {
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.inspector-controls {
  background-color: var(--stk-text-30);
  color: var(--stk-text-70);
  font-family: var(--stk-family-mono);
  font-size: 0.75em;
  position: sticky;
  top: 0;
  z-index: 1;

  strong {
    color: #fff;
    font-weight: 500;
  }
}

.controls-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.inspector-info {
  display: flex;
  align-items: center;
  gap: 0.5em;
  padding: 0 1em 1em;
}

.inspector-details {
  padding: 0.5em 1em;
}

.pages {
  display: flex;
  align-items: center;
}

.current-page {
  display: flex;
  align-items: center;
  padding: 0 0.5em 0 1em;
  gap: 0.25em;
}

.page-prev,
.page-next {
  color: #fff;
  height: 2rem;
  width: 2rem;

  &:hover {
    background-color: var(--stk-primary);
    color: #fff;
  }
}

.inspector-fields {
  flex-grow: 1;
  overflow-y: auto;
}

.inspector-pagination {
  box-shadow: 0 0 0.5em rgb(0 0 0 / 10%);
  background-color: var(--stk-text-30);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1em;
  flex-shrink: 0;
  position: relative;
}

.inspector-validation {
  padding: 1rem;
}
</style>
